import styled from "styled-components";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { Icon, Input } from "semantic-ui-react";
import Filters from "./Filters";
import QuestionDropdown from "./QuestionDropdown";
import RadioButtonGroup from "./components/RadioButton";
import HorizontalBarChart from "components/Charts/HorizontalBarChart";
import { convertFeedbackData } from "components/Charts/barChartUtils";

const chartOptions = [
  { name: "Very Negative", color: "#de2121" },
  { name: "Slightly Negative", color: "#F57373" },
  { name: "Neutral", color: "#F7B531" },
  { name: "Slightly Positive", color: "#80C7A0" },
  { name: "Very Positive", color: "#3D8B67" },
];

const sentiments = [
  { id: "Very Negative", value: "Very Negative", name: "Very Negative" },
  {
    id: "Slightly Negative",
    value: "Slightly Negative",
    name: "Slightly Negative",
  },
  { id: "Neutral", value: "Neutral", name: "Neutral" },
  {
    id: "Slightly Positive",
    value: "Slightly Positive",
    name: "Slightly Positive",
  },
  { id: "Very Positive", value: "Very Positive", name: "Very Positive" },
];

const FeedbackTag = ({
  data = [],
  outcomeQ,
  isOTP,
  selectedQuestion,
  setQuestion,
  showComments,
  feedbackType,
  setFeedbackType,

  // Multi-select sentiment handling:
  selectedSentiments = [],
  setSelectedSentiments,
  displayedFeedback,
  setSelectedOutcomes,
  
  // Search functionality
  search,
  setSearch,
  setActivePage,
  showEmptyPage,
}) => {
  const [chartData, setChartData] = useState([]);
  const [immutableChartData, setImmutableChartData] = useState([]);
  const [actionablePercentages, setActionablePercentages] = useState([]);
  const [loading, setLoading] = useState(false);

  const { core_data } = useSelector(
    (state) => ({
      core_data: state.audit?.core_data,
    }),
    shallowEqual
  );

  // For comment questions if needed
  const [commentQuestions, setCommentQuestions] = useState([]);
  const originalDataRef = useRef();

  useEffect(() => {
    if (core_data?.questions?.comments?.length > 0 && !isOTP) {
      // If we aren't OTP use the commentQuestions found in the core_data?.questions?.comments
      const allOption = {
        id: null,
        value: null,
        name: "All Comments",
      };

      const questionOptions = core_data?.questions?.comments?.map((q) => {
        return {
          id: q.id,
          value: q.id,
          name: q.question,
        };
      });

      setCommentQuestions([allOption, ...questionOptions]);
    }
  }, [core_data]);

  // Convert your data to chart data
  useEffect(() => {
    setLoading(true);

    // Summarize sentiments
    const sentimentData = {};
    data.forEach((item) => {
      const s = item?.nlp?.sentiment;
      const isAction = item?.nlp?.action_orientation === "action_oriented";
      if (s) {
        if (!sentimentData[s]) {
          sentimentData[s] = { total: 0, action_oriented: 0 };
        }
        sentimentData[s].total++;
        if (isAction) sentimentData[s].action_oriented++;
      }
    });

    // Keep consistent order
    const sentimentOrder = [
      "Very Negative",
      "Slightly Negative",
      "Neutral",
      "Slightly Positive",
      "Very Positive",
    ];

    // Build series
    const _series = sentimentOrder.map((s) => {
      const total = sentimentData[s]?.total || 0;
      return { name: s, data: [total] };
    });

    // Calculate action-oriented percentages
    const _actionable = sentimentOrder.map((s) => {
      const total = sentimentData[s]?.total || 0;
      const actionOriented = sentimentData[s]?.action_oriented || 0;
      return total > 0 ? ((actionOriented / total) * 100).toFixed(1) : "0.0";
    });

    // Convert to chart friendly
    const cData = convertFeedbackData(_series, chartOptions, _actionable);
    setChartData(cData);
    setImmutableChartData(cData);
    setActionablePercentages(_actionable);

    setLoading(false);
  }, [data]);

  // Example of your overall feedback percentage usage
  const getFeedbackPercentage = () => {
    let total = 0;
    let action = 0;
    data.forEach((item) => {
      if (item?.nlp?.action_orientation === "action_oriented") action++;
      total++;
    });
    return total === 0 ? 0 : (action / total) * 100;
  };

  // Filter config for your "Filters" component
  const feedbackFiltersConfig = [
    {
      id: "outcome",
      title: "Outcomes",
      data: outcomeQ?.questions,
      callback: setSelectedOutcomes,
    },
    {
      id: "sentiment",
      title: "Sentiment",
      data: sentiments,
      // Instead of setting one "sentiment",
      // we pass an array of sentiments back to the parent
      callback: (selectedArray) => {
        setSelectedSentiments(selectedArray);
      },
    },
  ];

  // Handle user toggling a bar from the chart
  const handleBarSelect = (name) => {
    // “name” is e.g. "Very Negative"
    // Check if it's already selected
    if (selectedSentiments.includes(name)) {
      // remove it
      const newArray = selectedSentiments.filter((s) => s !== name);
      setSelectedSentiments(newArray);
    } else {
      // add it
      setSelectedSentiments([...selectedSentiments, name]);
    }
  };

  // Determine if we should show tabs - use existing logic
  // Show tabs if: not OTP, has comments available, and has actual feedback data
  const shouldShowTabs = !isOTP && showComments && data?.length > 0;

  return (
    <Container hasFeedback={displayedFeedback?.length > 0}>
      {shouldShowTabs && (
        <TabContainer>
          <Tab 
            active={feedbackType === 0} 
            onClick={() => setFeedbackType(0)}
          >
            Feedback
          </Tab>
          <Tab 
            active={feedbackType === 1} 
            onClick={() => setFeedbackType(1)}
          >
            Comments
          </Tab>
        </TabContainer>
      )}

      {/* Sentiment Distribution Chart */}
      {!loading &&
        immutableChartData?.length > 0 &&
        ((displayedFeedback?.length > 0 && selectedSentiments?.length === 0) ||
          selectedSentiments?.length > 0) && (
          <ChartContainer>
            <ChartTitle>Sentiment Distribution</ChartTitle>
            <HorizontalBarChart
              data={immutableChartData}
              actionablePercentages={actionablePercentages}
              selectedSentiments={selectedSentiments}
              onSelect={(barName) => handleBarSelect(barName)}
            />
          </ChartContainer>
        )}

      <DropdownSection>
        {(isOTP || feedbackType === 1) && (
          <QuestionDropdownContainer>
            <QuestionDropdown
              label="Filter by question"
              selectedOption={selectedQuestion}
              setSelected={(val) => setQuestion(val)}
              options={commentQuestions}
            />
          </QuestionDropdownContainer>
        )}
        <SearchContainer isFullWidth={!(isOTP || feedbackType === 1)}>
          <SearchLabel>Search</SearchLabel>
          <StyledInput
            icon={{ name: "search" }}
            placeholder="Search by keyword"
            value={search}
            onChange={(e) => {
              setActivePage(1);
              setSearch(e.target.value);
            }}
          />
        </SearchContainer>
      </DropdownSection>

      <FiltersSection>
        <Filters
          hideFactors={false}
          filters={feedbackFiltersConfig}
          outcomes={outcomeQ?.questions}
          onSelectOutcomes={setSelectedOutcomes}
          barSentiments={selectedSentiments}
          onSentimentFilter={(selected) => setSelectedSentiments(selected)}
          // If your filter component can handle multi-check for sentiments,
          // it should call `callback([arrayOfSelected])`.
        />
      </FiltersSection>
    </Container>
  );
};

export default FeedbackTag;

/* --- Styles simplified --- */

const Container = styled.div`
  margin-bottom: ${({ hasFeedback }) => (hasFeedback ? "16px" : "0")};
  position: relative;
  background: transparent;
`;

const FiltersSection = styled.div`
  margin-top: 8px;
  padding-top: 8px;
`;

const ChartContainer = styled.div`
  margin: 16px 0;
  width: 100%;
  background: white;
  border: 1px solid #f3f4f6;
  border-radius: 12px;
  padding: 16px;
`;

const ChartTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1a1d29;
  margin: 0 0 16px 0;
  line-height: 1.3;
`;

const DropdownSection = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 16px;
  margin-bottom: 8px;
  padding: 16px 0;
`;

const SearchContainer = styled.div`
  flex: ${({ isFullWidth }) => isFullWidth ? '1' : '0 0 30%'};
`;

const QuestionDropdownContainer = styled.div`
  flex: 0 0 70%;
  min-width: 0;
  position: relative;
`;

const SearchLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
`;

const StyledInput = styled(Input)`
  &&& {
    width: 100%;
    border-radius: 8px;
    background-color: white;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    input {
      border: none;
      background: none;
      font-size: 14px;
      color: #374151;
      padding: 12px 16px;
      &::placeholder {
        color: #9ca3af;
      }
    }

    .icon {
      margin-right: 16px;
      color: #6b7280;
    }

    &:focus-within {
      border-color: #2d70e2;
      box-shadow: 0 0 0 3px rgba(45, 112, 226, 0.1);
    }
  }
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 16px;
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ active }) => active ? '#2d70e2' : '#6b7280'};
  border-bottom: 2px solid ${({ active }) => active ? '#2d70e2' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${({ active }) => active ? '#2d70e2' : '#374151'};
    background-color: ${({ active }) => active ? 'transparent' : '#f9fafb'};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(45, 112, 226, 0.1);
  }
`;
